import React, { InputHTMLAttributes } from 'react'
import MaskedInput from 'react-text-mask'
import * as S from './styles'
// import { DefaultTheme } from 'styled-components'
import theme from 'presentation/styles/theme'

export type TextFieldProps = {
  onInputChange?: (value: string) => void
  label?: string
  mask?: (string | RegExp)[] | ((value: string) => (string | RegExp)[])
  initialValue?: string | number | undefined
  labelIcon?: React.ReactNode
  onLabelIconClick?: () => void
  icon?: React.ReactNode
  iconPosition?: 'left' | 'right'
  iconLocale?: 'inside' | 'outside'
  onIconClick?: (param?: any) => void
  disabled?: boolean
  error?: string | false
  bgColor?: 'mainBg' | 'white' | 'lightestGray'
  dsBgColor?: keyof typeof theme.ds.colors
  required?: boolean
  iconMargin?: string
  // outline?: boolean
  // outlineColor?: 'primary' | 'gray'
  // labelSize?: keyof DefaultTheme['font']['sizes']
  labelColor?: keyof typeof theme.colors
  dsLabelColor?: keyof typeof theme.ds.colors
  guide?: boolean
  onResetTableValue?: () => any
  regex?: RegExp
  iconDisabled?: boolean
} & InputHTMLAttributes<HTMLInputElement>

const TextField: React.ForwardRefRenderFunction<
  HTMLInputElement,
  TextFieldProps
> = (
  {
    label,
    name,
    mask,
    error,
    disabled = false,
    onInputChange,
    style,
    bgColor = 'white',
    dsBgColor = 'white',
    icon,
    iconPosition,
    onIconClick,
    required = false,
    iconLocale,
    iconMargin,
    labelColor,
    dsLabelColor = 'neutral900',
    // labelSize = 'small',
    onResetTableValue,
    labelIcon,
    onLabelIconClick,
    guide,
    regex,
    iconDisabled = false,
    ...props
  }: TextFieldProps,
  ref
) => {
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.currentTarget.value
    if (onResetTableValue && newValue === '') onResetTableValue()

    if (newValue && regex && !regex.test(newValue)) {
      return
    }

    !!onInputChange && onInputChange(newValue)
    !!props.onChange && props.onChange(e)
  }

  const iconClick = () => {
    !!onIconClick && onIconClick()
  }

  const labelIconClick = () => {
    !!onLabelIconClick && onLabelIconClick()
  }

  return (
    <S.Wrapper style={style} disabled={disabled} error={!!error}>
      {(!!label || !!labelIcon) && (
        <S.LabelWrapper>
          {!!label && (
            <S.Label
              htmlFor={name}
              labelColor={labelColor}
              dsLabelColor={dsLabelColor}
            >
              {label}
              {required ? <S.Asterisk>*</S.Asterisk> : ''}
            </S.Label>
          )}
          {!!labelIcon && (
            <S.LabelIcon onClick={labelIconClick}>{labelIcon}</S.LabelIcon>
          )}
        </S.LabelWrapper>
      )}
      <S.InputWrapper>
        {!!icon && (
          <S.Icon
            type="button"
            onClick={iconClick}
            iconPosition={iconPosition}
            iconLocale={iconLocale}
            iconMargin={iconMargin}
            disabled={iconDisabled}
          >
            {icon}
          </S.Icon>
        )}
        {mask ? (
          <MaskedInput
            type="text"
            mask={mask}
            guide={guide ?? true}
            disabled={disabled}
            name={name}
            {...props}
            onChange={onChange}
            render={(ref, props) => (
              <S.Input
                ref={(input) => ref(input!)}
                bgColor={bgColor}
                dsBgColor={dsBgColor}
                {...(label ? { id: name } : {})}
                {...props}
              />
            )}
          />
        ) : (
          <S.Input
            type="text"
            disabled={disabled}
            name={name}
            bgColor={bgColor}
            dsBgColor={dsBgColor}
            ref={ref}
            iconPosition={iconPosition}
            {...(label ? { id: name } : {})}
            {...props}
            onChange={onChange}
          />
        )}
      </S.InputWrapper>
      {!!error && <S.Error className="error">{error}</S.Error>}
    </S.Wrapper>
  )
}

export default React.forwardRef(TextField)
